import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Clock } from 'lucide-react';
import type { ChatMessage } from './ChatThread';
import { MODEL_VIEW_PREFIX_RE } from './chat-thread-grouping';

export interface ModelViewEditGroupProps {
  namedRange: string;
  messages: ChatMessage[];
}

const SCROLL_TO_SYSTEM_MESSAGE_EVENT = 'arcanum:scroll-to-system-message';

/**
 * Collapsed-by-default group renderer for adjacent `[model-view:*]` system
 * messages — in-platform-model-view Phase 4 Session 4. Click to expand;
 * expanded view shows each underlying message's timestamp + body in a
 * compact vertical list (no `<MessageBubble>` wrapping, per spec §3 keep
 * the visual treatment muted).
 *
 * Listens for the `arcanum:scroll-to-system-message` window event
 * (dispatched by Session 3's `<EditSyncedToast>` when the user clicks
 * "View in Conversation"). If the event's `systemMessageId` matches any
 * of the group's underlying message ids, the group expands itself + scrolls
 * its DOM root into view.
 */
export function ModelViewEditGroup({ namedRange, messages }: ModelViewEditGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const messageIds = messages.map((m) => m.id);

  // Listen for cross-component scroll-to-system-message events. The toast
  // CTA in `<EditSyncedToast>` dispatches one with the systemMessageId of
  // the just-committed edit; if any of our underlying messages match, we
  // expand and scroll into view.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ systemMessageId?: string }>).detail;
      if (!detail?.systemMessageId) return;
      if (messageIds.includes(detail.systemMessageId)) {
        setExpanded(true);
        // Defer the scroll until React commits the expansion so the layout
        // height stabilizes first.
        requestAnimationFrame(() => {
          rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    };
    window.addEventListener(SCROLL_TO_SYSTEM_MESSAGE_EVENT, handler);
    return () => window.removeEventListener(SCROLL_TO_SYSTEM_MESSAGE_EVENT, handler);
  }, [messageIds]);

  // Strip the `[model-view:${namedRange}] ` prefix from each underlying
  // message body for the expanded list — the prefix is metadata, not user-
  // facing text. The unparsed remainder reads e.g. `Exit Cap Rate changed
  // to 5.80% via model view`.
  const lastBody = stripPrefix(messages[messages.length - 1]?.content ?? '');

  return (
    <div
      ref={rootRef}
      className="ml-8 max-w-[65%]"
      data-testid={`mv-edit-group-${namedRange}`}
      data-expanded={expanded ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-2 w-full text-left bg-transparent border-none cursor-pointer p-2 rounded-[2px] hover:bg-panel-bg transition-colors"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown size={12} className="text-text-dim shrink-0" />
        ) : (
          <ChevronRight size={12} className="text-text-dim shrink-0" />
        )}
        <Clock size={12} className="text-text-dim shrink-0" />
        <span className="text-xs font-mono text-text-muted truncate">
          {namedRange} · {messages.length} edits in 30s
          {!expanded && ` · last: ${lastBody}`}
        </span>
      </button>
      {expanded && (
        <ul className="mt-1 space-y-1 pl-7 border-l border-panel-border ml-1">
          {messages.map((msg) => (
            <li
              key={msg.id}
              className="text-xs font-mono text-text-muted leading-relaxed"
              data-testid={`mv-edit-group-row-${msg.id}`}
            >
              <span className="text-text-dim mr-2">{formatRelativeStamp(msg)}</span>
              {stripPrefix(msg.content)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function stripPrefix(content: string): string {
  return content.replace(MODEL_VIEW_PREFIX_RE, '');
}

function formatRelativeStamp(msg: ChatMessage): string {
  const stamp = (msg as ChatMessage & { created_at?: string }).created_at;
  if (typeof stamp !== 'string') return '';
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return '';
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
